import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inputCls } from "@/components/kit";
import { MenuSheet } from "@/components/MenuSheet";
import { Booklet } from "@/components/MenuBooklet";
import { STYLES } from "@/components/MenuOrnaments";
import { api, errMsg } from "@/lib/api";

const LOGOS = [["afrodite", "Afrodite"], ["ledo", "Tenuta Ledo"], ["catering", "Catering"], ["primerano", "Primerano"]];
const BACK = { preset: () => "/listino", quote: (id) => `/preventivi/${id}`, contract: (id) => `/contratti/${id}` };

function Choice({ label, value, options, onChange, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-[#7B929C]">{label}</div>
      {options?.length > 0 && <div className="flex flex-wrap gap-1.5">{options.map((o, i) => <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testId}-opt-${i}`} className={`text-xs rounded-full px-3 py-1 border transition-colors ${value === o ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{o}</button>)}</div>}
      <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} />
    </div>
  );
}

function print(node, landscape) {
  const w = window.open("", "_blank");
  const head = [...document.querySelectorAll("style, link[rel=stylesheet]")].map((n) => n.outerHTML).join("");
  w.document.write(`<html><head><title>Menù</title>${head}<style>@page{size:A4 ${landscape ? "landscape" : "portrait"};margin:0}html,body{margin:0;background:#fff}#print-clone{display:block}</style></head><body><div id="print-clone">${node.outerHTML}</div></body></html>`);
  w.document.close();
  w.onload = () => setTimeout(() => { w.focus(); w.print(); }, 600);
}

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const longDate = (d) => (d ? `${+d.slice(8, 10)} ${MESI[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}` : "");
const initials = (n) => (n || "").split(/\s*(?:&|\be\b|,)\s*/i).filter(Boolean).slice(0, 2).map((x) => x.trim()[0] || "").join("").toUpperCase();
const SEDE = { afrodite: "Afrodite", ledo: "Tenuta Ledo", catering: "noi", primerano: "noi" };

function defaults(r, s, src) {
  const tipo = r.evento_tipo || "Evento";
  const nomi = src === "preset" ? "" : r.nome || "";
  return {
    formato: s.formato || "foglio", benvenuto_titolo: s.benvenuto_titolo || `Benvenuti a ${SEDE[r.logo] || "noi"}`,
    benvenuto: s.benvenuto || "La nostra cucina celebra l'armonia e l'incanto dei sensi.\nLasciatevi viziare dall'ospitalità della nostra casa\ne dai sapori del nostro territorio.",
    augurio: s.augurio ?? "Buon appetito!", occasione: s.occasione || (tipo === "Matrimonio" ? "Il matrimonio di" : `${tipo} di`), nomi: s.nomi || nomi,
    data_txt: s.data_txt || longDate(r.data), ringraziamento: s.ringraziamento || "", firma: s.firma || nomi, monogramma: s.monogramma ?? initials(nomi),
  };
}

function BookletFields({ art, set, p }) {
  const f = (k, label, opts) => <Choice key={k} label={label} value={art[k]} options={opts} onChange={(v) => set(k === "nomi" ? { nomi: v, ...(art.firma === art.nomi ? { firma: v } : {}), ...(art.monogramma === initials(art.nomi) ? { monogramma: initials(v) } : {}) } : { [k]: v })} testId={`menuart-${k}`} />;
  return (
    <div className="space-y-4 rounded-lg bg-[#F5FAFB] p-3" data-testid="booklet-fields">
      {f("occasione", "Copertina • occasione")}{f("nomi", "Copertina • nomi")}{f("data_txt", "Copertina • data")}{f("monogramma", "Monogramma (iniziali, vuoto = nessuno)")}
      {f("benvenuto_titolo", "Interno • titolo benvenuto")}
      <Area label="Interno • testo di benvenuto" value={art.benvenuto} options={p.benvenuti} onChange={(v) => set({ benvenuto: v })} testId="menuart-benvenuto" />
      {f("augurio", "Interno • augurio")}
      <Area label="Retro • ringraziamento degli sposi / festeggiati" value={art.ringraziamento} options={p.ringraziamenti} onChange={(v) => set({ ringraziamento: v })} testId="menuart-ringraziamento" />
      {f("firma", "Retro • firma")}
    </div>
  );
}

function Area({ label, value, options, onChange, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-[#7B929C]">{label}</div>
      {(options || []).map((o, i) => <button key={i} type="button" onClick={() => onChange(o)} data-testid={`${testId}-opt-${i}`} className={`block w-full text-left text-xs rounded-lg px-3 py-2 border ${value === o ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "bg-white border-[#D4E6EC]"}`}>{o}</button>)}
      <textarea rows={4} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} />
    </div>
  );
}

export default function MenuArt() {
  const { src, id } = useParams();
  const [info, setInfo] = useState(null);
  const [art, setArt] = useState(null);
  const [logos, setLogos] = useState([]);
  const [names, setNames] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sheet = useRef(null);
  useEffect(() => {
    api.get(`/menu-art/${src}/${id}`).then((r) => {
      setInfo(r.data);
      const s = r.data.saved || {};
      setArt({ stile: s.stile || "classico", titolo: s.titolo || "Menù", sottotitolo: s.sottotitolo || "", chiusura: s.chiusura || "", logo: s.logo || r.data.logo, sezioni: s.sezioni || {}, proposte: s.proposte || {}, ...defaults(r.data, s, src) });
    }).catch((e) => setErr(errMsg(e)));
    api.get("/public/logos").then((r) => setLogos(r.data));
    api.get("/public/brand-names").then((r) => setNames(r.data)).catch(() => {});
  }, [src, id]);
  if (err) return <div className="space-y-3" data-testid="menuart-error"><div className="text-sm text-[#991B1B]">{err}</div><Link to="/listino" className="text-sm text-[#0B6E8F] hover:underline">Torna al listino</Link></div>;
  if (!info || !art) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const set = (patch) => setArt({ ...art, ...patch });
  const p = art.proposte;
  const book = art.formato === "libretto";
  const logoOk = art.logo !== "none" && logos.includes(art.logo);
  const suggest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/menu-art/${src}/${id}/suggest`);
      const x = r.data;
      setArt((a) => ({ ...a, proposte: x, titolo: x.titoli[0] || a.titolo, sottotitolo: x.sottotitoli[0] || a.sottotitolo, chiusura: x.chiusure[0] || a.chiusura,
        benvenuto: x.benvenuti?.[0] || a.benvenuto, ringraziamento: x.ringraziamenti?.[0] || a.ringraziamento,
        sezioni: Object.fromEntries(info.portate.map((q) => [q.key, x.sezioni[q.key]?.[0] || a.sezioni[q.key] || q.label])) }));
      toast.success("Proposte pronte: scegli quelle che preferisci");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    try {
      await api.put(`/menu-art/${src}/${id}`, art);
      toast.success("Menù artistico salvato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 fade-up">
        <div>
          <Link to={BACK[src]?.(id) || "/"} className="text-xs text-[#7B929C] inline-flex items-center gap-1" data-testid="menuart-back"><ArrowLeft size={13} /> Indietro</Link>
          <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Menù artistico</h1>
          <div className="text-sm text-[#47606B]">{info.evento_tipo}{info.nome && ` • ${info.nome}`}{info.sede && ` • ${info.sede}`}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={suggest} disabled={busy || !info.portate.length} data-testid="menuart-suggest">{busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}{busy ? "L'IA sta scrivendo…" : "Suggerisci titoli con IA"}</Button>
          <Button variant="outline" className="rounded-full" onClick={save} data-testid="menuart-save"><Save size={15} className="mr-2" /> Salva</Button>
          <Button className="rounded-full" onClick={() => { save(); print(sheet.current, book); }} data-testid="menuart-print"><Printer size={15} className="mr-2" /> Stampa / PDF</Button>
        </div>
      </div>
      {!info.portate.length && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="menuart-empty">Questo menù non ha ancora portate: compilale prima.</div>}
      <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
        <div className="rounded-xl border border-[#D4E6EC] bg-white p-5 space-y-5" data-testid="menuart-controls">
          <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-wider text-[#7B929C]">Formato</div>
            <div className="grid grid-cols-2 gap-2">{[["foglio", "Foglio singolo A4"], ["libretto", "Libretto 4 facciate"]].map(([k, l]) => <button key={k} type="button" onClick={() => set({ formato: k })} data-testid={`menuart-format-${k}`} className={`rounded-lg border-2 px-3 py-2 text-sm transition-colors ${art.formato === k ? "border-[#0B6E8F] bg-[#EAF5F7]" : "border-[#D4E6EC]"}`}>{l}</button>)}</div>
            {book && <p className="text-xs text-[#47606B]">A4 orizzontale piegato a metà. Stampa fronte/retro «lato corto»: foglio 1 fuori, foglio 2 dentro.</p>}
          </div>
          <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-wider text-[#7B929C]">Stile</div>
            <div className="grid grid-cols-2 gap-2">{Object.entries(STYLES).map(([k, s]) => <button key={k} type="button" onClick={() => set({ stile: k })} data-testid={`menuart-style-${k}`} className={`rounded-lg border-2 px-3 py-2 text-sm text-left transition-colors ${art.stile === k ? "border-[#0B6E8F] bg-[#EAF5F7]" : "border-[#D4E6EC]"}`}><span style={{ fontFamily: s.head, color: s.title, fontSize: s.script ? 20 : 16 }}>{s.label}</span></button>)}</div>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-wider text-[#7B929C]">Logo</div>
            <select className={inputCls} value={art.logo} onChange={(e) => set({ logo: e.target.value })} data-testid="menuart-logo">{LOGOS.map(([k, l]) => <option key={k} value={k}>{names[k] || l}{logos.includes(k) ? "" : " (scritta elegante, logo non caricato)"}</option>)}<option value="none">Nessun logo</option></select>
          </div>
          <Choice label="Titolo del menù" value={art.titolo} options={p.titoli} onChange={(v) => set({ titolo: v })} testId="menuart-title" />
          <Choice label="Frase sotto il titolo" value={art.sottotitolo} options={p.sottotitoli} onChange={(v) => set({ sottotitolo: v })} testId="menuart-subtitle" />
          {info.portate.map((q) => <Choice key={q.key} label={q.label} value={art.sezioni[q.key] ?? q.label} options={p.sezioni?.[q.key]} onChange={(v) => set({ sezioni: { ...art.sezioni, [q.key]: v } })} testId={`menuart-sec-${q.key}`} />)}
          <Choice label="Chiusura" value={art.chiusura} options={p.chiusure} onChange={(v) => set({ chiusura: v })} testId="menuart-closing" />
          {book && <BookletFields art={art} set={set} p={p} />}
        </div>
        <div className="overflow-auto rounded-xl bg-[#EEF2F3] p-4 flex justify-center">
          {book ? (
            <div style={{ width: "calc(297mm * 0.5)", height: "calc(420mm * 0.5 + 60px)" }}>
              <div className="origin-top-left" style={{ transform: "scale(0.5)" }}><div ref={sheet}><Booklet art={art} info={info} ok={logoOk} /></div></div>
            </div>
          ) : (
          <div style={{ width: "calc(210mm * 0.62)", height: "calc(297mm * 0.62)" }}>
            <div className="shadow-2xl origin-top-left" style={{ transform: "scale(0.62)" }}><MenuSheet ref={sheet} art={art} info={info} logoOk={logoOk} /></div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
